/**
 * WB3-5 — focus + intent aware assistive AI request payload builder.
 * WB4 — Inspector T0 signal enrichment (exceptions · flows · activity) for summary quality.
 */

import type { WorkbenchFocus } from '@shared/business-workbench/workbench-focus';
import { ASSISTIVE_ONLY_MODE } from '@shared/ai/assistive-only-policy';

export interface AssistiveEntityContext {
  id: string;
  name?: string;
  status?: string;
  businessDomain?: string;
}

/** Inspector T0 signals — encoded into sourceText for richer descriptive summaries. */
export interface AssistiveInspectorContext {
  exceptionCount?: number;
  exceptionSummary?: string;
  arBalance?: number | string;
  lastActivityAt?: string;
  relatedFlowCount?: number;
  threadCount?: number;
}

export interface AssistivePayloadInput {
  tenantId: string;
  focus?: WorkbenchFocus;
  entity?: AssistiveEntityContext;
  inspectorContext?: AssistiveInspectorContext;
  sourceText?: string;
  capabilityAction?: string;
  capabilityDomain?: string;
  taskName?: string;
  taskDescription?: string;
}

export interface AssistiveGatewayBody {
  tenantId: string;
  assistiveMode: typeof ASSISTIVE_ONLY_MODE;
  sourceText?: string;
  capabilityAction?: string;
  capabilityDomain?: string;
  taskName?: string;
  taskDescription?: string;
}

function buildFocusLine(focus: WorkbenchFocus): string {
  const root = `${focus.root.businessDomain}/${focus.root.id}`;
  return `focus intent=${focus.intent} mode=${focus.mode} variant=${focus.variant} root=${root}`;
}

function buildEntityLine(entity: AssistiveEntityContext): string {
  const name = entity.name?.trim() || '(unnamed)';
  const status = entity.status?.trim() || 'unknown';
  const domain = entity.businessDomain?.trim() || 'unknown';
  return `object ${domain}/${entity.id} status=${status} name=${name}`;
}

function buildInspectorContextLine(context: AssistiveInspectorContext): string | undefined {
  const parts: string[] = [];

  if (context.exceptionCount !== undefined) {
    parts.push(`exceptions=${context.exceptionCount}`);
  }
  if (context.exceptionSummary?.trim()) {
    parts.push(`exceptionSummary=${context.exceptionSummary.trim()}`);
  }
  if (context.arBalance !== undefined && String(context.arBalance).trim().length > 0) {
    parts.push(`arBalance=${context.arBalance}`);
  }
  if (context.lastActivityAt?.trim()) {
    parts.push(`lastActivity=${context.lastActivityAt.trim()}`);
  }
  if (context.relatedFlowCount !== undefined) {
    parts.push(`relatedFlows=${context.relatedFlowCount}`);
  }
  if (context.threadCount !== undefined) {
    parts.push(`threads=${context.threadCount}`);
  }

  return parts.length > 0 ? `inspector ${parts.join(' ')}` : undefined;
}

/** Builds API-safe body with focus/intent encoded in sourceText for gateway context. */
export function buildAssistiveGatewayPayload(input: AssistivePayloadInput): AssistiveGatewayBody {
  const lines = [
    input.sourceText?.trim(),
    input.entity ? buildEntityLine(input.entity) : undefined,
    input.inspectorContext ? buildInspectorContextLine(input.inspectorContext) : undefined,
  ];
  if (input.focus) {
    lines.push(buildFocusLine(input.focus));
  }

  const sourceText = lines.filter((line): line is string => Boolean(line?.length)).join('\n');

  return {
    tenantId: input.tenantId,
    assistiveMode: ASSISTIVE_ONLY_MODE,
    ...(sourceText.length > 0 ? { sourceText } : {}),
    ...(input.capabilityAction !== undefined ? { capabilityAction: input.capabilityAction } : {}),
    ...(input.capabilityDomain !== undefined ? { capabilityDomain: input.capabilityDomain } : {}),
    ...(input.taskName !== undefined ? { taskName: input.taskName } : {}),
    ...(input.taskDescription !== undefined ? { taskDescription: input.taskDescription } : {}),
  };
}

export function buildExplainSourceText(input: {
  staticReason: string;
  actionId: string;
  actionLabel?: string;
  focus?: WorkbenchFocus;
  entity?: AssistiveEntityContext;
  inspectorContext?: AssistiveInspectorContext;
}): string {
  const lines = [
    `Explain why workbench action "${input.actionId}"${input.actionLabel ? ` (${input.actionLabel})` : ''} is disabled.`,
    `Static reason: ${input.staticReason}`,
  ];
  if (input.entity) {
    lines.push(buildEntityLine(input.entity));
  }
  if (input.inspectorContext) {
    const inspectorLine = buildInspectorContextLine(input.inspectorContext);
    if (inspectorLine) {
      lines.push(inspectorLine);
    }
  }
  if (input.focus) {
    lines.push(buildFocusLine(input.focus));
  }
  return lines.join('\n');
}
